/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { requestStatusType } from '../../types';
import { importFile } from '../../services/fileSystemService';

interface ImportState {
  status: requestStatusType;
  error: string | null;
  isValidating: boolean;
  failedModal: {
    isOpen: boolean;
    errorMessage: string;
    invalidUrl: string | null;
  };
}

interface ImportUrlThunkArg {
  teamId: string;
  folderId: string;
  url: string;
  intent?: string;
}

// Basic frontend URL validation
export const isBasicUrlValid = (url: string): boolean => {
  // Trim whitespace
  const trimmedUrl = url.trim();

  // Basic sanity checks
  if (!trimmedUrl || trimmedUrl.includes(' ') || !trimmedUrl.includes('.')) {
    return false;
  }

  try {
    const urlObj = new URL(trimmedUrl);

    // Allow http, https, and screenapp protocols
    if (
      !urlObj.protocol.startsWith('http') &&
      urlObj.protocol !== 'screenapp:'
    ) {
      return false;
    }

    // Validate hostname structure (should have at least two parts)
    const hostnameParts = urlObj.hostname.split('.');
    if (hostnameParts.length < 2) {
      return false;
    }

    // Check if any hostname part is empty
    if (hostnameParts.some((part) => !part)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const initialState: ImportState = {
  status: 'idle',
  error: null,
  isValidating: false,
  failedModal: {
    isOpen: false,
    errorMessage: '',
    invalidUrl: null,
  },
} as ImportState;

const importSlice = createSlice({
  name: 'import',
  initialState,
  reducers: {
    resetImportState: (state) => {
      state.status = 'idle';
      state.error = null;
      state.isValidating = false;
      state.failedModal = initialState.failedModal;
    },
    reset: (state) => {
      state.status = 'idle';
      state.error = null;
      state.isValidating = false;
      state.failedModal = initialState.failedModal;
    },
    setImportFailedModal: (
      state,
      action: { payload: { message: string; url: string } }
    ) => {
      state.status = 'failed';
      state.error = action.payload.message;
      state.isValidating = false;
      state.failedModal = {
        isOpen: true,
        errorMessage: action.payload.message,
        invalidUrl: action.payload.url,
      };
    },
    closeImportFailedModal: (state) => {
      state.failedModal = initialState.failedModal;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase('import/importUrl/pending', (state) => {
        state.status = 'pending';
        state.error = null;
        state.isValidating = true;
        state.failedModal = initialState.failedModal;
      })
      .addCase('import/importUrl/fulfilled', (state) => {
        state.status = 'succeeded';
        state.error = null;
        state.isValidating = false;
        state.failedModal = initialState.failedModal;
      })
      .addCase('import/importUrl/rejected', (state, action: any) => {
        state.status = 'failed';
        state.error =
          action.payload?.message || action.error.message || 'Import failed';
        state.isValidating = false;
        state.failedModal = {
          isOpen: true,
          errorMessage:
            action.payload?.message || action.error.message || 'Import failed',
          invalidUrl: action.payload?.url || null,
        };
      });
  },
});

export const {
  resetImportState,
  reset,
  setImportFailedModal,
  closeImportFailedModal,
} = importSlice.actions;

// Create the thunk
export const importUrl = createAsyncThunk<
  void,
  ImportUrlThunkArg,
  { rejectValue: { message: string; url: string } }
>(
  'import/importUrl',
  async (
    { teamId, folderId, url, intent }: ImportUrlThunkArg,
    { dispatch, rejectWithValue }
  ) => {
    // Basic frontend validation first
    if (!url) {
      const error = { message: 'URL cannot be empty', url };
      dispatch(setImportFailedModal(error));
      return rejectWithValue(error);
    }

    // Trim URL before validation
    const trimmedUrl = url.trim();

    if (!isBasicUrlValid(trimmedUrl)) {
      const error = {
        message:
          'Please enter a valid URL (Ex: https://www.instagram.com/reel/DHLwa2faw)',
        url: trimmedUrl,
      };
      dispatch(setImportFailedModal(error));
      return rejectWithValue(error);
    }

    try {
      // Start import immediately without waiting for files to load
      const response = await importFile(teamId, folderId, trimmedUrl, {
        intent,
      });

      // Check if the API response indicates success
      if (!response.data.success) {
        const errorMessage =
          response.data.message || 'Failed to import file. Please try again.';
        const errorData = { message: errorMessage, url: trimmedUrl };
        dispatch(setImportFailedModal(errorData));
        return rejectWithValue(errorData);
      }

      return undefined;
    } catch (error) {
      // Handle specific backend error responses
      let errorMessage = 'Failed to import file. Please try again.';

      if (error.response?.data?.message) {
        // Use backend error message if available
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMessage =
          'The requested content was not found. Please check the URL.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access to this content is restricted.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required to access this content.';
      } else if (!navigator.onLine) {
        errorMessage = 'Please check your internet connection and try again.';
      }

      const errorData = { message: errorMessage, url: trimmedUrl };
      dispatch(setImportFailedModal(errorData));
      return rejectWithValue(errorData);
    }
  }
);

export default importSlice.reducer;
